/*
 ╔═══════════════════════════════════════════════════════════════════════════════╗
 ║  onemedia Frontend — React 19 SPA (Portfolio Project)                         ║
 ║                                                                               ║
 ║  A responsive, accessible single-page application showcasing:                ║
 ║  • Modern React patterns (hooks, context, async/await)                       ║
 ║  • Component composition with Tailwind + Radix UI                            ║
 ║  • Real-time updates via WebSocket                                           ║
 ║  • Advanced analytics & performance tracking                                 ║
 ║  • Admin dashboard with full content management                              ║
 ║  • Mobile-first responsive design                                            ║
 ║                                                                               ║
 ║  PAGES:                                                                       ║
 ║  ├─ Home          : Featured posts + hero section                            ║
 ║  ├─ Article       : Full post view with sharing                              ║
 ║  ├─ Timeline      : Chronological post archive                               ║
 ║  ├─ AdminLogin    : Secure JWT-based authentication                         ║
 ║  ├─ AdminDashboard: Content management, analytics, settings                  ║
 ║  └─ AdminModeration: IP blocks, abuse prevention                             ║
 ║                                                                               ║
 ║  DEPLOYMENT: npm run build → deploy to Vercel, Netlify, or your server      ║
 ║  FORK THIS: Customize styles, components, and content for your platform     ║
 ╚═══════════════════════════════════════════════════════════════════════════════╝
*/

import React, { useEffect, useState } from "react";
import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import Article from "./pages/Article";
import Timeline from "./pages/Timeline";
import Privacy from "./pages/Privacy";
import Legal from "./pages/Legal";
import Restricted from "./pages/Restricted";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminEditPost from "./pages/AdminEditPost";
import AdminBranding from "./pages/AdminBranding";
import AdminHeaderModules from "./pages/AdminHeaderModules";
import AdminBackup from "./pages/AdminBackup";
import AdminNewsletter from "./pages/AdminNewsletter";
import AdminModeration from "./pages/AdminModeration";
import AgeGate from "./pages/AgeGate";
import AdminFab from "./components/AdminFab";
import { BrandBackdrop, BrandingProvider } from "./components/BrandedSection";
import SwUpdateBanner from "./components/SwUpdateBanner";
import CookieBanner from "./components/CookieBanner";
import { Toaster } from "sonner";
import { trackPageView } from "./lib/analytics";
import api from "./lib/api";

function NotFound() {
  return (
    <div className="xs-page" style={{ textAlign: "center", padding: 80 }}>
      <h1 style={{ color: "#ffde16" }}>404</h1>
      <p>this page does not exist.</p>
      <a href="/" className="xs-btn">return home</a>
    </div>
  );
}

function RouteTracker() {
  const loc = useLocation();
  useEffect(() => {
    trackPageView(loc.pathname);
  }, [loc.pathname]);
  return null;
}

/**
 * Detects if the API blocks this visitor (X-Blocked header on any 403).
 * If detected, swaps the entire app for the Restricted page.
 */
function useBlockProbe() {
  const [blocked, setBlocked] = useState(null);
  const [reason, setReason] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/").catch((err) => {
      if (!mounted) return;
      if (err.response?.status === 403 && err.response?.headers?.["x-blocked"] === "1") {
        setBlocked(true);
        setReason(err.response?.headers?.["x-blocked-reason"] || null);
      }
    });
    // also intercept future 403s
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 403 && err?.response?.headers?.["x-blocked"] === "1") {
          if (mounted) {
            setBlocked(true);
            setReason(err?.response?.headers?.["x-blocked-reason"] || null);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => { mounted = false; api.interceptors.response.eject(id); };
  }, []);
  return { blocked, reason };
}

export default function App() {
  const { blocked, reason } = useBlockProbe();
  if (blocked) return <Restricted reason={reason} />;
  return (
    <div className="App">
      <BrandingProvider>
        <BrandBackdrop />
        <div className="xs-app-content">
          <BrowserRouter>
            <RouteTracker />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/posts" element={<Timeline />} />
              <Route path="/articles/:slug" element={<Article />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/restricted" element={<Restricted />} />
              <Route path="/age-gate" element={<AgeGate />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/new" element={<AdminEditPost />} />
              <Route path="/admin/edit/:id" element={<AdminEditPost />} />
              <Route path="/admin/branding" element={<AdminBranding />} />
              <Route path="/admin/header-modules" element={<AdminHeaderModules />} />
              <Route path="/admin/backup" element={<AdminBackup />} />
              <Route path="/admin/newsletter" element={<AdminNewsletter />} />
              <Route path="/admin/moderation" element={<AdminModeration />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <AdminFab />
            <SwUpdateBanner />
            <CookieBanner />
            <Toaster position="top-right" theme="dark" richColors closeButton />
          </BrowserRouter>
        </div>
      </BrandingProvider>
    </div>
  );
}
